import useCarrotPlan from "components/carrotplans/hooks/useCarrotPlan"
import { StatusCodes } from "http-status-codes"
import { useHistory } from "react-router-dom"
import { CarrotPlan } from "types/CarrotPlan"
import { RoutingInfo } from "types/RoutingInfo"
import ResponseError from "utils/ResponseError"
import { useGetPastRoutingInfo } from "./useGetPastRoutingInfo"
import { getMatchingRoutingInfoForCarrotPlan } from "../../carrotplans/utils/getMatchingRoutingInfoForCarrotPlan"
import useRoutingInfo from "./useRoutingInfo"
import useCarrotPlanStepMenuItems, { CarrotPlanStepMenuItem } from "../../carrotplans/hooks/useCarrotPlanStepMenuItems"
import useCarrotPlans from "../../carrotplans/hooks/useCarrotPlans"
import moment from "moment-timezone"

type UseViewCarrotPlan = {
  carrotPlan: CarrotPlan
  carrotPlanStepMenuItems: CarrotPlanStepMenuItem[]
  matchingRoutingInfo: RoutingInfo
  pastRoutingInfo: RoutingInfo[]
  couldRoutingInfoHaveBeenUsedAlready: boolean
  areAnyLoading: boolean
  carrotPlanError: ResponseError
  menuItemsError: ResponseError
}

export const useViewCarrotPlan = (planId: string, employeeId: number): UseViewCarrotPlan => {
  const { data: carrotPlan, isLoading: isLoadingCarrotPlan, error: carrotPlanError } = useCarrotPlan(planId)
  const {
    data: carrotPlanStepMenuItems,
    isLoading: loadingCarrotPlanMenuItems,
    error: menuItemsError
  } = useCarrotPlanStepMenuItems(carrotPlan?.carrotPlanId)

  const { data: pastRoutingInfo, isLoading: isRoutingPastLoading } = useGetPastRoutingInfo(carrotPlan?.employeeId)
  const { data: mostRecentRoutingInfo, isLoading: isRoutingLoading } = useRoutingInfo(carrotPlan?.employeeId)
  const { data: carrotPlans, isLoading: isCarrotPlansListLoading } = useCarrotPlans(employeeId)

  const matchingRoutingInfo = getMatchingRoutingInfoForCarrotPlan({
    carrotPlan,
    mostRecentRoutingInfo,
    pastRoutingInfo
  })

  // Check if the matching routing info was available for a previous CP
  let couldRoutingInfoHaveBeenUsedAlready = false
  if (matchingRoutingInfo?.routingCompletedDate) {
    const routingCompleteDate = moment(matchingRoutingInfo.routingCompletedDate)
    const selectedPlanCreatedAtUtc = moment(carrotPlan.createdAtUtc)
    carrotPlans?.forEach((plan) => {
      const comparisonPlanCreatedAtUtc = moment(plan.createdAtUtc)
      if (
        comparisonPlanCreatedAtUtc.isAfter(routingCompleteDate) &&
        comparisonPlanCreatedAtUtc.isBefore(selectedPlanCreatedAtUtc)
      ) {
        couldRoutingInfoHaveBeenUsedAlready = true
      }
    })
  }

  const history = useHistory()
  if (carrotPlanError) {
    switch (carrotPlanError.statusCode) {
      case StatusCodes.NOT_FOUND:
        history.replace("/404")
        return null
    }
  }

  const areAnyLoading =
    isLoadingCarrotPlan ||
    loadingCarrotPlanMenuItems ||
    isRoutingPastLoading ||
    isRoutingLoading ||
    isCarrotPlansListLoading

  return {
    carrotPlan,
    carrotPlanStepMenuItems,
    matchingRoutingInfo,
    pastRoutingInfo,
    couldRoutingInfoHaveBeenUsedAlready,
    areAnyLoading,
    carrotPlanError,
    menuItemsError
  }
}
